import React, { useMemo, useState } from 'react';
import type { IconKind } from '../Icon/Icon';
import { Icon } from '../Icon/Icon';
import { GROWTH_SETUP_META } from '../../growth-setup/config';
import { SU_CATEGORIES, SU_OWNER, SU_STATUS } from '../../growth-setup/mockData';
import type { SuCategory, SuOwnerKey, SuStatusKey, SuTask, SuToneKey } from '../../growth-setup/types';
import './growth-setup.css';

const SU_TONE: Record<SuToneKey, { accent: string; tint: string }> = {
  ai: { accent: 'var(--ai-3)', tint: 'var(--ai-tint)' },
  info: { accent: 'var(--info)', tint: 'var(--info-tint)' },
  success: { accent: 'var(--success)', tint: 'var(--success-tint)' },
  gold: { accent: 'var(--gold)', tint: 'var(--gold-tint)' },
  terra: { accent: 'var(--terracotta)', tint: 'var(--terracotta-tint)' },
  lav: { accent: 'var(--ai-4)', tint: 'var(--ai-tint)' },
  neutral: { accent: 'var(--ink-3)', tint: 'var(--surface-mute)' },
};

const suEyebrowClass =
  'text-[10px] font-bold uppercase tracking-[0.08em] text-[var(--ink-3)]';

const RING_R = 58;
const RING_C = 2 * Math.PI * RING_R;

function SuStatusIcon({ status, size = 28 }: { status: SuStatusKey; size?: number }) {
  const meta = SU_STATUS[status];
  const tone = SU_TONE[meta.tone];

  const inner =
    meta.icon === 'check' ? (
      <Icon kind="check" size={13} stroke={3} />
    ) : meta.icon === 'spin' ? (
      <span className="su-spin">
        <Icon kind="refresh" size={12} stroke={2.4} />
      </span>
    ) : meta.icon === 'alert' ? (
      <Icon kind="alert" size={13} stroke={2.4} />
    ) : (
      <span className="h-[7px] w-[7px] rounded-full" style={{ background: tone.accent }} />
    );

  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-full"
      style={{
        width: size,
        height: size,
        background: tone.tint,
        border: `1.5px solid color-mix(in srgb, ${tone.accent} 40%, transparent)`,
        color: tone.accent,
      }}
    >
      {inner}
    </span>
  );
}

function SuStatusPill({ status }: { status: SuStatusKey }) {
  const meta = SU_STATUS[status];
  const tone = SU_TONE[meta.tone];
  return (
    <span
      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-[var(--r-pill)] px-[11px] py-1 text-[11px] font-bold"
      style={{ background: tone.tint, color: tone.accent }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: tone.accent }} />
      {meta.label}
    </span>
  );
}

function SuOwner({ owner }: { owner: SuOwnerKey }) {
  const meta = SU_OWNER[owner];
  const tone = SU_TONE[meta.tone];
  return (
    <span className="inline-flex items-center gap-[5px] text-[11px] font-semibold text-[var(--ink-3)]">
      <Icon kind={meta.icon} size={11} stroke={2} style={{ color: tone.accent }} />
      {meta.label}
    </span>
  );
}

function MiniStat({
  count,
  status,
  danger,
}: {
  count: number;
  status: SuStatusKey;
  danger?: boolean;
}) {
  const meta = SU_STATUS[status];
  const tone = SU_TONE[meta.tone];
  return (
    <div className="flex items-center gap-[7px]">
      <span className="h-2 w-2 rounded-full" style={{ background: tone.accent }} />
      <span
        className="text-xs"
        style={{ color: danger ? tone.accent : 'var(--ink-2)', fontWeight: danger ? 600 : 400 }}
      >
        <b className="font-bold text-[var(--ink-0)]">{count}</b> {meta.label.toLowerCase()}
      </span>
    </div>
  );
}

function SuActionGroup({
  tone,
  owner,
  eyebrow,
  title,
  note,
  tasks,
  btn,
  btnIcon,
  loading = false,
  onAct,
}: {
  tone: SuToneKey;
  owner: SuOwnerKey;
  eyebrow: string;
  title: string;
  note?: string;
  tasks: SuTask[];
  btn: string;
  btnIcon?: IconKind;
  loading?: boolean;
  onAct: (task: SuTask) => void;
}) {
  const t = SU_TONE[tone];
  const ownerMeta = SU_OWNER[owner];

  return (
    <div
      className="rounded-[var(--r-xl)] p-5"
      style={{
        background: t.tint,
        border: `1px solid color-mix(in srgb, ${t.accent} 26%, transparent)`,
      }}
    >
      <div className="flex items-start gap-[15px]">
        <span
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[11px]"
          style={{
            background: `color-mix(in srgb, ${t.accent} 18%, transparent)`,
            color: t.accent,
          }}
        >
          <Icon kind={ownerMeta.icon} size={19} stroke={1.9} />
        </span>
        <div className="min-w-0 flex-1">
          <div
            className="mb-1 text-[10.5px] font-bold uppercase tracking-[0.1em]"
            style={{ color: t.accent }}
          >
            {eyebrow}
          </div>
          <h3 className="m-0 font-[var(--font-display)] text-[17px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
            {title}
          </h3>
          {note ? (
            <p className="m-0 mt-1.5 text-[12.5px] leading-[1.5] text-[var(--ink-2)]">{note}</p>
          ) : null}
          <div className="mt-3 flex flex-col gap-2">
            {tasks.map((task) => (
              <div
                key={task.id}
                className="su-actionrow flex items-center gap-3 rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3.5 py-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="mb-0.5 text-[13.5px] font-semibold text-[var(--ink-0)]">
                    {task.title}
                  </div>
                  <div className="text-xs leading-[1.45] text-[var(--ink-3)]">{task.detail}</div>
                </div>
                <button
                  type="button"
                  onClick={() => onAct(task)}
                  disabled={loading}
                  className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-[var(--r-md)] border-0 px-[15px] py-2 font-[var(--font-body)] text-[12.5px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                  style={{ background: t.accent }}
                >
                  {btnIcon ? <Icon kind={btnIcon} size={13} stroke={2} /> : null}
                  {btn}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export interface SetupChecklistViewProps {
  categories?: SuCategory[];
  loading?: boolean;
  isUnseeded?: boolean;
  onTaskAction?: (taskKey: string) => void;
  onContactWebmaster?: () => void;
}

export function SetupChecklistView({
  categories: categoriesProp,
  loading = false,
  isUnseeded = false,
  onTaskAction,
  onContactWebmaster,
}: SetupChecklistViewProps = {}) {
  const meta = GROWTH_SETUP_META;
  const categories = categoriesProp ?? SU_CATEGORIES;
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 2000);
  };

  const handleTaskAction = (task: SuTask) => {
    if (onTaskAction) {
      onTaskAction(task.taskKey);
      return;
    }
    showToast('Opening review…');
  };

  const handleContactWebmaster = () => {
    if (onContactWebmaster) {
      onContactWebmaster();
      return;
    }
    showToast('ELVA will email your webmaster');
  };

  const allTasks = useMemo(() => categories.flatMap((category) => category.tasks), [categories]);
  const total = allTasks.length;
  const done = allTasks.filter((task) => task.status === 'done').length;
  const pct = isUnseeded || total === 0 ? 0 : Math.round((done / total) * 100);
  const ready = !isUnseeded && total > 0 && pct >= 80;

  const dentistTasks = allTasks.filter((task) => task.status === 'waiting_on_you');
  const webmasterTasks = allTasks.filter((task) => task.status === 'waiting_on_webmaster');
  const inProgressTasks = allTasks.filter((task) => task.status === 'in_progress');
  const brokenTasks = allTasks.filter((task) => task.status === 'broken');

  const ringColor =
    isUnseeded || total === 0 ? 'var(--ink-3)' : pct >= 80 ? 'var(--success)' : pct >= 50 ? 'var(--ai-3)' : 'var(--gold)';

  return (
    <div className="growth-setup-root relative mx-auto w-full max-w-[940px] px-[30px] pb-[90px] pt-[26px]">
      <div className="mb-[22px]">
        <div className="mb-2 flex items-center gap-2">
          <Icon kind="check-circ" size={13} stroke={2} className="text-[var(--ai-3)]" />
          <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
            {meta.eyebrow}
          </span>
        </div>
        <h1 className="m-0 font-[var(--font-display)] text-[clamp(26px,3vw,33px)] font-semibold leading-[1.08] tracking-[-0.035em] text-[var(--ink-0)]">
          {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
        </h1>
        <p className="mt-2.5 max-w-[680px] text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
          {meta.description}
        </p>
      </div>

      <div className="relative mb-[22px] flex flex-wrap items-center gap-7 overflow-hidden rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] p-[26px_30px] shadow-[var(--sh-1)]">
        <div className="pointer-events-none absolute -right-[30px] -top-[70px] h-[200px] w-[200px] rounded-full bg-[var(--grad-spectrum)] opacity-[0.12] blur-[64px]" />
        <div className="relative h-[130px] w-[130px] shrink-0">
          <svg width="130" height="130" className="-rotate-90">
            <circle cx="65" cy="65" r={RING_R} fill="none" stroke="var(--surface-mute)" strokeWidth="10" />
            <circle
              cx="65"
              cy="65"
              r={RING_R}
              fill="none"
              stroke={ringColor}
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray={RING_C}
              strokeDashoffset={RING_C * (1 - pct / 100)}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="font-[var(--font-display)] text-[32px] font-extrabold leading-none tracking-[-0.04em] text-[var(--ink-0)]">
              {pct}%
            </div>
            <div className={`${suEyebrowClass} mt-1`}>Complete</div>
          </div>
        </div>
        <div className="relative min-w-[280px] flex-1">
          <div
            className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.12em]"
            style={{ color: ringColor }}
          >
            {ready ? 'Module is ready' : 'Getting set up'}
          </div>
          <h2 className="m-0 font-[var(--font-display)] text-[21px] font-semibold leading-[1.2] tracking-[-0.025em] text-[var(--ink-0)]">
            {isUnseeded
              ? meta.unseededTitle
              : ready
                ? meta.readyTitle
                : total > 0
                  ? `${done} of ${total} setup tasks complete`
                  : meta.unseededTitle}
          </h2>
          <p className="mb-3.5 mt-2 text-[13px] leading-[1.6] text-[var(--ink-2)]">
            {isUnseeded
              ? meta.unseededDescription
              : ready
                ? meta.readyDescription
                : meta.progressDescription}
          </p>
          <div className="flex flex-wrap gap-5">
            {dentistTasks.length > 0 ? (
              <MiniStat count={dentistTasks.length} status="waiting_on_you" />
            ) : null}
            {webmasterTasks.length > 0 ? (
              <MiniStat count={webmasterTasks.length} status="waiting_on_webmaster" />
            ) : null}
            {inProgressTasks.length > 0 ? (
              <MiniStat count={inProgressTasks.length} status="in_progress" />
            ) : null}
            {brokenTasks.length > 0 ? (
              <MiniStat count={brokenTasks.length} status="broken" danger />
            ) : null}
          </div>
        </div>
      </div>

      {dentistTasks.length > 0 || webmasterTasks.length > 0 ? (
        <div className="mb-6 flex flex-col gap-3">
          {dentistTasks.length > 0 ? (
            <SuActionGroup
              tone="gold"
              owner="dentist"
              eyebrow="Your action needed"
              title={`${dentistTasks.length} ${dentistTasks.length === 1 ? 'item needs' : 'items need'} your input`}
              tasks={dentistTasks}
              btn="Mark done"
              loading={loading}
              onAct={handleTaskAction}
            />
          ) : null}
          {webmasterTasks.length > 0 ? (
            <SuActionGroup
              tone="lav"
              owner="webmaster"
              eyebrow="Webmaster required"
              title={`${webmasterTasks.length} ${webmasterTasks.length === 1 ? 'item needs' : 'items need'} your webmaster`}
              note={meta.webmasterNote}
              tasks={webmasterTasks}
              btn="Have ELVA contact them"
              btnIcon="mail"
              loading={loading}
              onAct={(_task) => handleContactWebmaster()}
            />
          ) : null}
        </div>
      ) : null}

      {isUnseeded ? (
        <div className="mb-6 rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-6 py-8 text-center shadow-[var(--sh-1)]">
          <Icon kind="info" size={22} stroke={1.9} className="mx-auto mb-3 text-[var(--ink-3)]" />
          <p className="m-0 text-[14px] leading-[1.55] text-[var(--ink-2)]">{meta.unseededDescription}</p>
        </div>
      ) : null}

      <div className="mb-3.5">
        <h2 className="m-0 font-[var(--font-display)] text-lg font-semibold tracking-[-0.025em] text-[var(--ink-0)]">
          {meta.integrationsTitle}
        </h2>
        <p className="m-0 mt-[3px] text-[12.5px] text-[var(--ink-3)]">{meta.integrationsSub}</p>
      </div>

      <div className="flex flex-col gap-3">
        {categories.map((category) => {
          const categoryDone = category.tasks.filter((task) => task.status === 'done').length;
          const allDone = categoryDone === category.tasks.length;

          return (
            <div
              key={category.id}
              className="overflow-hidden rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]"
            >
              <div
                className="flex items-center gap-[13px] border-b border-[var(--cream-2)] px-5 py-[15px]"
                style={{ background: allDone ? 'var(--success-tint)' : 'var(--surface-mute)' }}
              >
                <span
                  className="inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px]"
                  style={{
                    background: allDone
                      ? 'color-mix(in srgb, var(--success) 18%, transparent)'
                      : 'var(--cream-1)',
                    color: allDone ? 'var(--success)' : 'var(--ink-2)',
                    border: allDone ? undefined : '1px solid var(--cream-2)',
                  }}
                >
                  <Icon kind={category.icon} size={18} stroke={1.8} />
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="m-0 font-[var(--font-display)] text-[15.5px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
                    {category.title}
                  </h3>
                  <div className="mt-px text-xs text-[var(--ink-3)]">{category.description}</div>
                </div>
                <span
                  className="inline-flex items-center gap-1.5 rounded-[var(--r-pill)] px-[11px] py-1 font-[var(--font-mono)] text-xs font-bold"
                  style={{
                    background: allDone
                      ? 'color-mix(in srgb, var(--success) 16%, transparent)'
                      : 'var(--cream-1)',
                    color: allDone ? 'var(--success)' : 'var(--ink-2)',
                    border: allDone ? undefined : '1px solid var(--cream-2)',
                  }}
                >
                  {allDone ? <Icon kind="check" size={11} stroke={3} /> : null}
                  {categoryDone}/{category.tasks.length}
                </span>
              </div>
              <div>
                {category.tasks.map((task, index) => (
                  <div
                    key={task.id}
                    className="su-task flex items-center gap-3.5 px-5 py-[13px] transition-[background] duration-[var(--dur-fast)]"
                    style={{
                      borderBottom:
                        index < category.tasks.length - 1 ? '1px solid var(--cream-2)' : undefined,
                    }}
                  >
                    <SuStatusIcon status={task.status} />
                    <div className="min-w-0 flex-1">
                      <div className="mb-0.5 flex flex-wrap items-center gap-2.5">
                        <span
                          className="text-[13.5px] font-semibold"
                          style={{
                            color: task.status === 'done' ? 'var(--ink-2)' : 'var(--ink-0)',
                          }}
                        >
                          {task.title}
                        </span>
                        <SuOwner owner={task.owner} />
                      </div>
                      <div className="text-xs leading-[1.5] text-[var(--ink-3)]">{task.detail}</div>
                    </div>
                    <SuStatusPill status={task.status} />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-7 flex items-center gap-[11px] rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-[18px] py-[15px]">
        <Icon kind="info" size={16} stroke={1.9} className="shrink-0 text-[var(--ink-3)]" />
        <div className="text-[12.5px] leading-[1.5] text-[var(--ink-2)]">{meta.footerNote}</div>
      </div>

      {toast ? (
        <div className="elva-pop fixed bottom-6 left-1/2 z-[250] flex -translate-x-1/2 items-center gap-2 rounded-[var(--r-md)] bg-[var(--ink-0)] px-[17px] py-[11px] font-[var(--font-body)] text-[12.5px] font-medium text-[var(--cream-1)] shadow-[var(--sh-2)]">
          <Icon kind="check" size={14} stroke={2.4} className="text-[var(--ai-5)]" />
          {toast}
        </div>
      ) : null}
    </div>
  );
}
